import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const UserIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const EnvelopeIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const AcademicCapIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
  </svg>
);

const HeartIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const StaffCard = ({ name, role, emailUrl, imageUrl }) => (
  <div className="flex flex-col items-center p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300">
    <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-4 border-school-gold/20 shadow-sm mb-6">
      {imageUrl ? (
        <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-school-blue/8 text-school-blue">
          <UserIcon className="w-12 h-12" />
        </div>
      )}
    </div>
    <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-2 tracking-tight text-center">
      {name}
    </h3>
    <p className="text-base text-school-goldDark font-medium mb-4">{role}</p>
    <a
      href={emailUrl}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-school-blue/8 text-school-blue text-sm font-semibold hover:bg-school-blue hover:text-white transition-colors"
    >
      <EnvelopeIcon />
      Send Email
    </a>
  </div>
);

const FeatureCard = ({ icon, title, description }) => (
  <div className="flex flex-col items-center text-center p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-school-blue/8 text-school-blue mb-6 shadow-sm">
      {icon}
    </div>
    <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-3 tracking-tight leading-tight">
      {title}
    </h3>
    <p className="text-sm md:text-base text-gray-600 leading-7 max-w-sm mx-auto">{description}</p>
  </div>
);

// ==================== MAIN COMPONENT ====================

const OutOfClassroom = () => {
  const staff = [
    {
      name: "Jordan Alonzo",
      role: "Teacher",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Jennifer Bondi",
      role: "Teacher",
      emailUrl: "/coming-soon",
      imageUrl: "https://3.files.edl.io/8f4d/22/10/31/190757-ad17c41d-44e3-4ae4-a2b3-4a6e3e2dd7e9.jpg"
    },
    {
      name: "J. Calderon",
      role: "OT/PT/APE",
      emailUrl: "mailto:jcalderon4@schools.nyc.gov",
      imageUrl: null
    },
    {
      name: "Daine Carey",
      role: "Teacher",
      emailUrl: "/coming-soon",
      imageUrl: "https://3.files.edl.io/53b1/22/10/31/190822-b2ddc218-251d-4935-a4fc-0812f092f106.png"
    },
    {
      name: "R. Cruz",
      role: "ENL",
      emailUrl: "mailto:rcruz34@schools.nyc.gov",
      imageUrl: null
    },
    {
      name: "Susan Holbrook",
      role: "Teacher",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Susan Kovacs",
      role: "OT",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Liz Marquez",
      role: "OT",
      emailUrl: "/coming-soon",
      imageUrl: "https://3.files.edl.io/f3c5/21/11/18/125541-93ab792e-2759-4cdd-b12c-e680c1fa67ea.jpg"
    },
    {
      name: "Maria Martin",
      role: "Teacher",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Mariana Mercado",
      role: "Teacher",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Teresa Noto",
      role: "Teacher",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Ashley Sorrentini",
      role: "Speech Therapy",
      emailUrl: "mailto:asorrentini@schools.nyc.gov",
      imageUrl: null
    }
  ];

  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-school-gold" aria-hidden="true" />
                Contact Us
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
                Out of Classroom Service Providers
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                Specialized support services for students beyond the traditional classroom
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== INTRODUCTION ==================== */}
        <section className="py-20 md:py-28 bg-[#F5EFE0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Our Services"
              title="Comprehensive Student Support"
              description="Our out of classroom service providers offer specialized instruction and therapeutic services to ensure every student receives the support they need to thrive"
            />
          </Container>
        </section>

        {/* ==================== SERVICES ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="What We Offer"
              title="Specialized Services"
              description="Targeted support to meet individual student needs"
            />

            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              <FeatureCard
                icon={<AcademicCapIcon />}
                title="Specialized Instruction"
                description="Individualized educational support tailored to each student's learning needs and goals."
              />

              <FeatureCard
                icon={<HeartIcon />}
                title="Occupational Therapy"
                description="Supporting students in developing fine motor skills, sensory processing, and daily living skills."
              />

              <FeatureCard
                icon={<UserIcon />}
                title="Individual Support"
                description="One-on-one and small group interventions to address specific learning challenges."
              />
            </div>
          </Container>
        </section>

        {/* ==================== STAFF SECTION ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Our Team"
              title="Meet Our Service Providers"
              description="Dedicated professionals committed to student success"
            />

            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {staff.map((member, index) => (
                <StaffCard key={index} {...member} />
              ))}
            </div>
          </Container>
        </section>

        {/* ==================== CTA SECTION ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Need Support?
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              Reach out to our service providers for specialized support and guidance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/coming-soon"
                className="px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                Contact Us
              </a>
              <a
                href="/"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue border-2 border-school-blue/20 font-medium hover:bg-white/50 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default OutOfClassroom;
